/**
 * @fileoverview Date and Time Utilities
 *
 * Provides date/time manipulation and formatting utilities for NFL game scheduling:
 * - Date parsing from game IDs (YYYYMMDD format)
 * - Week number calculation based on season start dates
 * - Rest days calculation between games
 * - Season year determination from dates
 * - Elapsed time formatting for performance metrics
 *
 * **Key Concepts:**
 * - NFL seasons span two calendar years (Sept → Feb)
 * - Week 1 starts on Thursday (season opener)
 * - Regular season = 18 weeks (17 games + 1 bye per team)
 * - Playoffs = Weeks 19-22 (Wild Card, Divisional, Conference, Super Bowl)
 * - Rest days include travel, practice, recovery time
 *
 * **Critical for:**
 * - Context generation (rest days affect predictions)
 * - Schedule management (week identification)
 * - Performance monitoring (execution time tracking)
 *
 * @module DateHelpers
 * @see {@link module:GameContextGenerator} - Uses rest days for context
 * @see {@link module:IdHelpers} - Works with date-containing IDs
 */

import {
  MONTHS,
  SeasonStartingDateString,
  WEEK1_2023,
  WEEK1_2024,
  WEEK1_2025,
  WEEK1_2026,
  WEEK1_2027,
  WEEK1_2028,
} from '../constants/date-constants';
import { DAYS_SINCE_LAST_SEASON, NFL_WEEKS_TOTAL } from '../constants/generic-constants';
import { GameWeek, Season } from '../constants/id-constants';
import { GameID, GameWeekKey, SeasonID, UID } from '../types/id.types';

/**
 * Given a date string, returns the SeasonID based on the current NFL season structure.
 *
 * @param {Date} date - JavaScript Date object (defaults to current date if not provided)
 *
 * @return {SeasonID} Season identifier string based on NFL season structure
 */
export const getCurrentSeasonID = (date: Date = new Date()): SeasonID => {
  const year = date.getFullYear();
  const month = date.getMonth();
  // NFL season starts in September
  return month >= MONTHS.SEP ? `${year}` : `${year - 1}`;
};

/**
 * Converts a date string to a JavaScript Date object.
 * Handles both hyphenated and non-hyphenated formats.
 *
 * **Input Formats Accepted:**
 * - `'YYYYMMDD'` → e.g., `'20250905'`
 * - `'YYYY-MM-DD'` → e.g., `'2025-09-05'`
 *
 * **Safari Compatibility:**
 * Uses numeric year, zero-based month, and day arguments rather than
 * browser-dependent date-string parsing, preserving midnight in local time.
 *
 * @param {string} date - Date string in YYYYMMDD or YYYY-MM-DD format
 *
 * @return {Date} JavaScript Date object (time set to midnight local)
 *
 * @example
 * const gameDate = convertStringToDate('20250905');
 * // Returns: Date object for September 5, 2025 00:00:00
 *
 * @example
 * const gameDate = convertStringToDate('2025-09-05');
 * // Returns: Same Date object (hyphens stripped automatically)
 */
export const convertStringToDate = (date: string): Date => {
  const dt = date.replace(/-/g, '');
  const year = Number(dt.slice(0, 4));
  const month = Number(dt.slice(4, 6)) - 1; // Months are zero-based in JS Date
  const day = Number(dt.slice(6, 8));
  return new Date(year, month, day);
};

/**
 * Determines which NFL season a date belongs to and returns Week 1 start date.
 * Handles season transitions that span two calendar years.
 *
 * **Season Logic:**
 * - NFL seasons start in September (e.g., 2025 season starts Sept 2025)
 * - Seasons end in February (e.g., 2025 season ends Feb 2026)
 * - Week 1 is the first Thursday night game (season kickoff)
 * - Dates are compared against season start thresholds
 *
 * **Supported Seasons:**
 * - 2023: Week 1 starts Sept 7, 2023
 * - 2024: Week 1 starts Sept 5, 2024
 * - 2025: Week 1 starts Sept 4, 2025
 * - 2026: Week 1 starts Sept 10, 2026
 * - 2027: Week 1 starts Sept 9, 2027
 * - 2028: Week 1 starts Sept 7, 2028
 *
 * **Use Cases:**
 * - Extracting season year from game IDs
 * - Calculating week numbers from dates
 * - Validating date ranges for season queries
 *
 * @param {string} date - Date string from gameID (format: 'YYYYMMDD')
 *
 * @return {Date} Week 1 start date for the season this date belongs to
 *
 * @see {@link getGameWeekKeyByDateString} - Uses this to calculate week numbers
 * @see {@link getSeasonIdFromGameID} - Uses this to extract season year
 *
 * @example
 * // Game in Week 5 of 2025 season
 * const week1Date = getBelongsToSeasonDate('20251002');
 * // Returns: Date object for Sept 4, 2025 (Week 1 of 2025 season)
 *
 * @example
 * // Super Bowl in 2026 (part of 2025 season)
 * const week1Date = getBelongsToSeasonDate('20260209');
 * // Returns: Date object for Sept 4, 2025 (still 2025 season)
 *
 * @example
 * // Future/past dates fallback
 * const week1Date = getBelongsToSeasonDate('20300101');
 * // Returns: Date object for Jan 1, 2030 (no season data, returns input)
 */
export const getBelongsToSeasonDate = (date: string): Date => {
  // try {
  if (date >= SeasonStartingDateString.Year2023) {
    if (date < SeasonStartingDateString.Year2024) {
      return WEEK1_2023;
    } else if (date < SeasonStartingDateString.Year2025) {
      return WEEK1_2024;
    } else if (date < SeasonStartingDateString.Year2026) {
      return WEEK1_2025;
    } else if (date < SeasonStartingDateString.Year2027) {
      return WEEK1_2026;
    } else if (date < SeasonStartingDateString.Year2028) {
      return WEEK1_2027;
    } else {
      return WEEK1_2028;
    }
  }
  // throw new Error(`Date ${date} is before supported seasons`);
  // } finally {
  return convertStringToDate(date);
  // }
};

/**
 * Calculates the NFL week number for a given date.
 * Handles special cases like Wednesday games and season boundaries.
 *
 * **Algorithm:**
 * 1. Determine which season the date belongs to (Thursday of Week 1)
 * 2. Shift back 1 day to make Wednesday the start of each week
 * 3. Calculate days elapsed since adjusted Week 1 start
 * 4. Divide by 7 to get week number (rounded up)
 * 5. Clamp to valid range (1-18)
 *
 * **Week Boundaries:**
 * - Week starts on **Wednesday** (aligns with NFL operational week)
 * - Wednesday-Tuesday = One complete week
 * - Thursday season opener is Day 1 of Week 1 (Wednesday before is Day 0)
 * - Example: Christmas Wednesday (Dec 25) → Starts Week 17 (not end of Week 16)
 * - Matches NFL roster moves, waivers, and practice schedules
 *
 * **Edge Cases:**
 * - Dates before Week 1 → Week 1
 * - Dates after Week 18 → Week 18 (playoffs use different system)
 * - Super Bowl in February → Week 18 (clamped)
 *
 * **Valid Weeks:**
 * - Regular Season: Weeks 1-18 (17 games + bye weeks)
 * - Playoffs: Weeks 19-22 (handled separately in app logic)
 *
 * @param {string} date - Date string in YYYYMMDD format
 *
 * @return {GameWeekKey} Week identifier (format: 'WeekX' where X = 1-18)
 *
 * @see {@link getBelongsToSeasonDate} - Used to find season start
 * @see {@link toSeasonWeekID} - Combines with year to create SeasonWeekID
 *
 * @example
 * // Season opener Thursday (Week 1 Day 1)
 * const weekKey = getGameWeekKeyByDateString('20250905');
 * // Returns: 'Week1'
 *
 * @example
 * // Wednesday game (starts new week)
 * const weekKey = getGameWeekKeyByDateString('20250903');
 * // Returns: 'Week1' (Wednesday = Day 0 of Week 1)
 *
 * @example
 * // Week 5 game
 * const weekKey = getGameWeekKeyByDateString('20251002');
 * // Returns: 'Week5'
 *
 * @example
 * // Christmas Wednesday special case (starts Week 17)
 * const weekKey = getGameWeekKeyByDateString('20241225');
 * // Returns: 'Week17' (Wednesday = beginning of Week 17)
 *
 * @example
 * // Super Bowl (February)
 * const weekKey = getGameWeekKeyByDateString('20260209');
 * // Returns: 'Week18' (clamped to max regular season)
 */
export const getGameWeekKeyByDateString = (date: string): GameWeekKey => {
  const oneDay = 1000 * 60 * 60 * 24;
  const gameDate = convertStringToDate(date);

  // Week 1 Thursday date from constants (e.g., Sept 5, 2024)
  const week1Thursday = getBelongsToSeasonDate(date);

  // Create a copy to avoid mutating the original date from getBelongsToSeasonDate
  const week1Wednesday = new Date(week1Thursday);

  // Shift back 1 day to make Wednesday the start of each week
  // This ensures Wednesday games (like Christmas) start a new week rather than ending the previous one
  // Week boundary: Wednesday 00:00 - Tuesday 23:59
  week1Wednesday.setDate(week1Wednesday.getDate() - 1);

  const diffTime = gameDate.setHours(0) - week1Wednesday.setHours(0);
  const diffDays = Math.ceil(diffTime / oneDay);

  // Add 1 to diffDays so that Week 1 start date (diffDays=0, the Wednesday) becomes Week 1,
  // and 7 days later (diffDays=7, next Wednesday) becomes Week 2
  const weekNum = Math.min(Math.max(1, Math.ceil(diffDays / 7)), NFL_WEEKS_TOTAL);

  const weekID: GameWeekKey = `Week${weekNum}` as GameWeekKey;

  return weekID;
};

/**
 * Converts seconds to a formatted `MM:SS` string.
 *
 * @param  {number} seconds - total value represented in seconds
 * @returns {string} formatted `string` as MM:SS
 */
export const formatValueAsDate = (seconds: number, divisor: number = 1): string => {
  const newSeconds = divisor > 1 ? Math.round(seconds / divisor) : seconds;

  const secs = String(newSeconds % 60).padStart(2, '0');
  const minutes = Math.floor(newSeconds / 60);
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}:${String(minutes - hours * 60).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`;
};

/**
 * Extracts the game date from a GameID.
 * GameIDs encode the date in the first 8 characters.
 *
 * **GameID Format:** `'YYYYMMDD_AWAY@HOME'`
 * - Date portion: `YYYYMMDD` (8 digits)
 * - Separator: `_`
 * - Teams: `AWAY@HOME` (team abbreviations)
 *
 * @param {GameID} gameID - Game identifier (e.g., '20250905_KC@BAL')
 *
 * @return {Date} JavaScript Date object for the game date
 *
 * @see {@link calculateRestDays} - Uses this to compute days between games
 *
 * @example
 * const gameDate = getGameDateByGameID('20250905_KC@BAL');
 * // Returns: Date object for September 5, 2025
 */
export const getGameDateByGameID = (gameID: GameID): Date => {
  const dateString = gameID.split('_')[0];
  return convertStringToDate(dateString);
};

/**
 * Calculates the number of rest days between two games for a team.
 * Critical for context-aware predictions (short rest impacts performance).
 *
 * **Definition of Rest Days:**
 * - Days from previous game to current game (inclusive of game days)
 * - Includes travel, practice, recovery time
 * - Does NOT include the current game day itself
 *
 * **Special Cases:**
 * - **Week 1**: No previous game → Returns `DAYS_SINCE_LAST_SEASON` (210 days = ~7 months)
 * - **Post-Bye Week**: Previous game skipped by caller (see `getPreviousGameID`)
 * - **Thursday Night**: 3-4 days rest (short week)
 * - **Sunday to Sunday**: 7 days rest (normal week)
 * - **Monday to Sunday**: 6 days rest (slightly less)
 *
 * **Context Model Impact:**
 * - Rest differential ≥3 days: `±0.5 points` per day of advantage
 * - Example: Team A (10 days) vs Team B (7 days) → +1.5 points to Team A
 *
 * **UTC Handling:**
 * Uses UTC timestamps to avoid daylight saving time issues.
 * Ensures consistent day counts regardless of local timezone.
 *
 * @param {GameID} currentGameID - Current game identifier (e.g., '20250912_KC@PHI')
 * @param {GameID} [previousGameID] - Previous game identifier (optional)
 *
 * @return {number} Number of days between games (0 if same day, 210 if Week 1)
 *
 * @see {@link getPreviousGameID} - Finds previous game, skipping bye weeks
 * @see {@link applyContextAdjustments} - Uses rest days in prediction model
 *
 * @example
 * // Normal week (Sunday to Sunday)
 * const restDays = calculateRestDays('20250914_KC@PHI', '20250907_KC@BAL');
 * // Returns: 7
 *
 * @example
 * // Thursday night game (short week)
 * const restDays = calculateRestDays('20250911_KC@DET', '20250907_KC@BAL');
 * // Returns: 4
 *
 * @example
 * // Week 1 (no previous game)
 * const restDays = calculateRestDays('20250905_KC@BAL');
 * // Returns: 210 (DAYS_SINCE_LAST_SEASON constant)
 *
 * @example
 * // Extended rest (mini-bye, international game, etc.)
 * const restDays = calculateRestDays('20251002_KC@NO', '20250921_KC@ATL');
 * // Returns: 11 (extra rest can be advantage)
 */
export const calculateRestDays = (currentGameID: GameID, previousGameID?: GameID): number => {
  if (!previousGameID) return DAYS_SINCE_LAST_SEASON; // assuming this is Week1 if no previous game date is specified
  const currentGameDate = getGameDateByGameID(currentGameID);
  const previousGameDate = getGameDateByGameID(previousGameID);

  // Use UTC to avoid DST issues
  const currentUTC = Date.UTC(currentGameDate.getFullYear(), currentGameDate.getMonth(), currentGameDate.getDate());
  const previousUTC = Date.UTC(previousGameDate.getFullYear(), previousGameDate.getMonth(), previousGameDate.getDate());

  const oneDay = 1000 * 60 * 60 * 24;
  const diffTime = currentUTC - previousUTC;
  const restDays = Math.round(diffTime / oneDay);
  return restDays === 0 ? 0 : restDays; // Ensure -0 becomes 0
};

export const isSeasonYearMatchUID = (uID: UID, year: SeasonID): boolean => {
  let orderedUID = uID;
  // If not in the new format (YYYY-...), treat as old UID
  if (!/^\d{4}-/.test(orderedUID)) {
    orderedUID = `${year}-${orderedUID}`;
  }
  return uID.startsWith(`${year}-`);
};

/**
 * Formats elapsed time in milliseconds to human-readable string.
 * Used for performance monitoring and batch job progress reporting.
 *
 * **Output Formats:**
 * - < 1 second: `'X ms'` (e.g., `'250 ms'`)
 * - < 1 minute: `'Xsec'` (e.g., `'45sec'`)
 * - < 1 hour: `'Xmin Ysec'` (e.g., `'5min 30sec'`)
 * - ≥ 1 hour: `'Xhr Ymin Zsec'` (e.g., `'2hr 15min 45sec'`)
 *
 * **Precision:**
 * - Milliseconds: Exact value
 * - Seconds: Rounded to nearest second
 * - Minutes/hours: Floor division (no decimal)
 *
 * **Common Use Cases:**
 * - Admin batch job duration: `'3min 42sec'`
 * - Prediction generation: `'1sec'`
 * - Data migration: `'1hr 23min 10sec'`
 * - Quick operations: `'120 ms'`
 *
 * @param {number} timeInMS - Elapsed time in milliseconds
 *
 * @return {string} Human-readable time string
 *
 * @example
 * // Fast operation
 * const formatted = formatElapsedTime(250);
 * // Returns: '250 ms'
 *
 * @example
 * // Under a minute
 * const formatted = formatElapsedTime(45000);
 * // Returns: '45sec'
 *
 * @example
 * // Multiple minutes
 * const formatted = formatElapsedTime(330000);
 * // Returns: '5min 30sec'
 *
 * @example
 * // Over an hour
 * const formatted = formatElapsedTime(7425000);
 * // Returns: '2hr 3min 45sec'
 *
 * @example
 * // Batch job reporting
 * const startTime = Date.now();
 * await processBatchJob();
 * const duration = Date.now() - startTime;
 * console.log(`Job completed in ${formatElapsedTime(duration)}`);
 * // Output: 'Job completed in 3min 42sec'
 */
export const formatElapsedTime = (timeInMS: number) => {
  if (timeInMS < 1000) return `${timeInMS} ms`;
  const seconds = timeInMS / 1000;
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}hr ${minutes % 60}min ${Math.round(seconds % 60)}sec`;
  if (minutes > 0) return `${minutes}min ${Math.round(seconds % 60)}sec`;
  return `${Math.round(seconds)}sec`;
};

/**
 * Converts Unix epoch time (seconds) to JavaScript milliseconds timestamp.
 * Used for Firestore timestamp conversions and date comparisons.
 *
 * **Unix Epoch Format:**
 * - Input: Seconds since January 1, 1970 00:00:00 UTC
 * - Output: Milliseconds since January 1, 1970 00:00:00 UTC
 *
 * **JavaScript Date Requirements:**
 * - JavaScript Date objects use milliseconds (not seconds)
 * - Firestore timestamps often return seconds
 * - Conversion: multiply seconds by 1000 (or use setUTCSeconds)
 *
 * **Use Cases:**
 * - Converting Firestore server timestamps to Date objects
 * - Comparing database timestamps with JavaScript dates
 * - Time-based sorting and filtering
 * - Audit trail timestamp conversion
 *
 * @param {number} epochTime - Unix timestamp in seconds (e.g., 1704067200)
 *
 * @return {number} Timestamp in milliseconds (e.g., 1704067200000)
 *
 * @see {@link getDateInMSFromEpoch} - Firestore timestamp handling
 *
 * @example
 * // Convert Firestore timestamp
 * const firestoreTimestamp = 1704067200; // Jan 1, 2024 00:00:00 UTC
 * const milliseconds = getDateInMSFromEpoch(firestoreTimestamp);
 * // Returns: 1704067200000
 *
 * @example
 * // Create Date object from epoch
 * const epochTime = 1704067200;
 * const date = new Date(getDateInMSFromEpoch(epochTime));
 * // Returns: Date object for Jan 1, 2024 00:00:00 UTC
 *
 * @example
 * // Time difference calculation
 * const startEpoch = 1704067200;
 * const endEpoch = 1704153600;
 * const durationMS = getDateInMSFromEpoch(endEpoch) - getDateInMSFromEpoch(startEpoch);
 * // Returns: 86400000 (24 hours in milliseconds)
 */
export const getDateInMSFromEpoch = (epochTime: number) => {
  const epTime = new Date(0);
  epTime.setUTCSeconds(epochTime);
  return epTime.getTime();
};

/**
 * Validates whether a season year is supported by the application.
 * Checks against the Season enum for available NFL seasons.
 *
 * **Supported Seasons:**
 * - Currently defined in `Season` enum (constants/id-constants.ts)
 * - Typically includes: 2023, 2024, 2025, 2026, 2027, 2028
 * - Each season has corresponding Week 1 date constants
 * - Each season has complete schedule and stats data
 *
 * **Validation Purpose:**
 * - Prevent queries for unavailable season data
 * - Guard route parameters and API requests
 * - Display user-friendly errors for invalid seasons
 * - Enable/disable UI controls based on data availability
 *
 * **Use Cases:**
 * - Route guards: Validate URL parameters before loading data
 * - Form validation: Dropdown/select season pickers
 * - API validation: Reject requests for unsupported years
 * - Feature flags: Enable predictions only for current season
 *
 * @param {SeasonID} year - Season year to validate (e.g., '2025')
 *
 * @return {boolean} `true` if season is supported, `false` otherwise
 *
 * @see {@link Season} - Enum defining all supported seasons
 * @see {@link getBelongsToSeasonDate} - Uses season constants
 *
 * @example
 * // Valid season
 * const isValid = isSupportedYear('2025');
 * // Returns: true
 *
 * @example
 * // Invalid season (too old)
 * const isValid = isSupportedYear('2020');
 * // Returns: false
 *
 * @example
 * // Invalid season (too far in future)
 * const isValid = isSupportedYear('2030');
 * // Returns: false
 *
 * @example
 * // Route guard usage
 * if (!isSupportedYear(seasonID)) {
 *   return <NotFoundPage message="Season data not available" />;
 * }
 *
 * @example
 * // API validation
 * if (!isSupportedYear(req.body.seasonID)) {
 *   throw new functions.https.HttpsError('invalid-argument', 'Unsupported season');
 * }
 */
export const isSupportedYear = (year: SeasonID): boolean => {
  return Object.values(Season).includes(year as Season);
};

/**
 * Validates whether a week identifier is a valid NFL game week.
 * Checks against the GameWeek enum for valid week values.
 *
 * **Valid Week Formats:**
 * - Regular Season: `'Week1'` through `'Week18'`
 * - Defined in `GameWeek` enum (constants/id-constants.ts)
 * - Must match exact format with capital 'W'
 * - No spaces, hyphens, or zero-padding
 *
 * **Week Types:**
 * - Weeks 1-18: Regular season (17 games + bye weeks)
 * - Week 19: Wild Card playoff round
 * - Week 20: Divisional playoff round
 * - Week 21: Conference championships
 * - Week 22: Super Bowl
 *
 * **Invalid Formats:**
 * - `'week1'` (lowercase)
 * - `'Week 1'` (space)
 * - `'Week-1'` (hyphen)
 * - `'1'` (number only)
 * - `'Week01'` (zero-padded)
 *
 * **Use Cases:**
 * - Route validation: Check URL parameters before queries
 * - Form validation: Week selection dropdowns
 * - API guards: Reject invalid week requests
 * - Data integrity: Ensure consistent week format
 *
 * @param {string} week - Week identifier to validate (e.g., 'Week5')
 *
 * @return {boolean} `true` if week is valid, `false` otherwise
 *
 * @see {@link GameWeek} - Enum defining all valid weeks
 * @see {@link toWeekID} - Converts various formats to GameWeek
 * @see {@link getGameWeekKeyByDateString} - Calculates week from date
 *
 * @example
 * // Valid regular season week
 * const isValid = isSupportedWeek('Week5');
 * // Returns: true
 *
 * @example
 * // Valid playoff week
 * const isValid = isSupportedWeek('Week19');
 * // Returns: true (Wild Card)
 *
 * @example
 * // Invalid format (lowercase)
 * const isValid = isSupportedWeek('week5');
 * // Returns: false
 *
 * @example
 * // Invalid format (space)
 * const isValid = isSupportedWeek('Week 5');
 * // Returns: false
 *
 * @example
 * // Invalid week number
 * const isValid = isSupportedWeek('Week25');
 * // Returns: false
 *
 * @example
 * // Route guard usage
 * if (!isSupportedWeek(weekParam)) {
 *   return <NotFoundPage message="Invalid week" />;
 * }
 *
 * @example
 * // API validation with conversion
 * const weekID = toWeekID(weekInput); // Normalizes format
 * if (!isSupportedWeek(weekID)) {
 *   throw new Error('Week out of range');
 * }
 */
export const isSupportedWeek = (week: string): boolean => {
  return Object.values(GameWeek).includes(week as GameWeek);
};

export const getFormattedGameDate = (dateStr?: string): string => {
  const gameDate = dateStr ? convertStringToDate(dateStr) : new Date();
  const monthStr = MONTHS[gameDate.getMonth()];
  const formattedDate = `${monthStr} ${gameDate.getDate()}, ${gameDate.getFullYear()}`;

  return formattedDate;
};

export const convertToSeconds = (time: string) => {
  const [minutes, seconds] = time.split(':');
  return parseInt(minutes) * 60 + parseInt(seconds);
};
